import ko from 'knockout';
import moment from 'moment';
import numeral from 'numeral';

import {
    QualityType,
    Competention,
    CostCentre,
    AttachmentType,
} from './databaseModels';
import { resolveCdnUrl } from '../cdn-url-resolver';
import { dataUriToBlob } from '../file-helper';
import { lookupAddress } from '../postalCodeApi';
import { CustomError, customFetch as fetch } from '../fetch';
import { SelectPicker } from '../custom-packages/choicesjs/selectPicker';
import {
    RateComponentSetDate,
    EmployerRequestHourlyRateInputModel,
    RequestHourlyRateAndCommissionRuleCombined,
} from './rateComponentSet';
import { orderBy } from 'lodash-es';

const INT32_MAX_VALUE = 2147483647;
const requestImagePlaceholder = '/images/svg/add-photo.svg';

interface RequestModelOptions {
    companyAddress: string;
    companyHouseNumber: string;
    companyPostalCode: string;
    companyCity: string;
    companyEmail: string;
    companyPhoneNumber: string;
    companyAvatarHash: string;
    companyHasOwnHourlyRate: boolean;
    canSetAgeSpecificHourlyRates: boolean;
    costCentreIsRequired: boolean;
    rateComponentSetDate: RateComponentSetDate;
    companyHasDocuments: boolean;
}

interface PreviewModel {
    title: string;
    subTitle: string;
    imageSrc: string;
    dateText?: string;
    timeText?: string;
    hourlyRateText: string;
    cityText: string;
    isVacancy: boolean;
}

export class RequestModel {
    id: KnockoutObservable<string>;
    options: RequestModelOptions;

    name: KnockoutObservable<string>;
    amount: KnockoutObservable<string>;
    bonusRate: KnockoutObservable<string>;
    companyHasOwnHourlyRate: KnockoutObservable<boolean>;
    rateComponentSetDate: KnockoutObservable<RateComponentSetDate>;

    canSetAgeSpecificHourlyRates: KnockoutObservable<boolean>;
    hourlyRates: KnockoutComputed<EmployerRequestHourlyRateInputModel[]>;
    singleHourlyRate: KnockoutObservable<EmployerRequestHourlyRateInputModel | null>;

    enableEmployeeClocks: KnockoutObservable<boolean>;
    clothingRequirements: KnockoutObservable<string>;

    travelExpenses: KnockoutObservable<boolean>;
    forFavorites: KnockoutObservable<boolean>;
    collaborationPreferenceAllowed: KnockoutObservable<boolean>;
    companyHasFavoriteEmployees: KnockoutObservable<boolean>;

    multipleDays: KnockoutObservable<boolean>;
    selectedDates: KnockoutObservableArray<moment.Moment>;

    date: KnockoutObservable<moment.Moment>;
    dateTo: KnockoutObservable<moment.Moment>;

    timePartFrom: KnockoutObservable<string>;
    timePartTo: KnockoutObservable<string>;
    requestDuration: KnockoutComputed<number | null>;

    selectedQualityType: KnockoutObservable<string>;
    selectedCompetentions: KnockoutObservableArray<string>;

    qualityTypes: KnockoutObservableArray<QualityType>;
    qualityTypesExpanded: KnockoutObservable<boolean>;
    qualityTypesToShow: KnockoutComputed<QualityType[]>;
    competentions: KnockoutObservableArray<Competention>;
    hasAcceptedRegistrations: KnockoutObservable<boolean>;

    imageInfoExpanded: KnockoutObservable<boolean>;
    image: KnockoutObservable<any>;
    imageHash: KnockoutObservable<string>;

    addImages: KnockoutObservable<boolean>;
    images: KnockoutObservableArray<AddOrEditRequestImageInputModel>;
    existingImages: KnockoutObservableArray<AddOrEditRequestImageInputModel>;

    extraInfo: KnockoutObservable<string>;

    addressInfoExpanded: KnockoutObservable<boolean>;
    address: KnockoutObservable<string>;
    houseNumber: KnockoutObservable<string>;
    postalCode: KnockoutObservable<string>;
    city: KnockoutObservable<string>;

    contactInfoExpanded: KnockoutObservable<boolean>;
    contactEmail: KnockoutObservable<string>;
    contactPhoneNumber: KnockoutObservable<string>;

    imageSrc: KnockoutComputed<any>;
    selectedQualityTypeName: KnockoutComputed<string>;
    selectedQualityTypeExplanation: KnockoutComputed<string | null>;
    filteredCompetentions: KnockoutComputed<Competention[]>;

    costCentres: KnockoutObservableArray<CostCentre>;
    showNewCostCentre: KnockoutObservable<boolean>;
    selectedCostCentre: KnockoutObservable<SelectPicker>;
    newCostCentre: KnockoutObservable<string>;

    useCompanyDocuments: KnockoutObservable<boolean>;
    companyHasDocuments: KnockoutObservable<boolean>;
    showNoCompanyDocumentsWarning: KnockoutComputed<boolean>;
    mapDocument: KnockoutObservable<RequestFileInputModel>;
    briefingDocument: KnockoutObservable<RequestFileInputModel>;
    extraDocuments: KnockoutObservableArray<RequestFileInputModel>;
    existingDocuments: KnockoutObservableArray<RequestFileModel>;

    errors: KnockoutObservableArray<CustomError>;

    previewImageSrc: KnockoutComputed<string>;
    preview: KnockoutComputed<PreviewModel>;

    isVacancy: KnockoutObservable<boolean>;
    closeDate: KnockoutObservable<moment.Moment>;
    daysOfWeek: KnockoutObservableArray<DaysOfWeek>;
    hoursAvailableMin: KnockoutObservable<number>;
    hoursAvailableMax: KnockoutObservable<number>;

    model: KnockoutObservable<any>;

    constructor(model: any, options: RequestModelOptions) {
        this.options = options;
        this.model = ko.observable(model);

        this.errors = ko.observableArray<CustomError>();
        this.qualityTypes = ko.observableArray<QualityType>(model.qualityTypes);
        this.qualityTypesExpanded = ko.observable<boolean>(false);
        this.qualityTypesToShow = ko.pureComputed(() => {
            if (this.qualityTypesExpanded()) {
                return this.qualityTypes();
            }

            const array = this.qualityTypes().filter(
                (qt, index) => index < 3 || qt.id == this.selectedQualityType(),
            );

            return array;
        });

        this.rateComponentSetDate = ko.observable(
            model.rateComponentSetDate ?? options.rateComponentSetDate,
        );

        this.companyHasOwnHourlyRate = ko.observable<boolean>(
            this.options.companyHasOwnHourlyRate,
        );
        this.canSetAgeSpecificHourlyRates = ko.observable(
            this.options.canSetAgeSpecificHourlyRates,
        );

        this.hourlyRates = ko.pureComputed<
            EmployerRequestHourlyRateInputModel[]
        >(() => {
            const model = this.model();
            const rateComponentSetDate = this.rateComponentSetDate();
            const canSetAgeSpecificHourlyRates =
                this.canSetAgeSpecificHourlyRates();

            const isModelEmpty = !model || Object.keys(model).length === 0;
            if (isModelEmpty && !rateComponentSetDate) {
                return [];
            }

            const sortByAgeThreshold = (a: any, b: any) =>
                a.ageThreshold - b.ageThreshold;

            if (model?.hourlyRates) {
                return model.hourlyRates
                    .sort(sortByAgeThreshold)
                    .map(
                        (rate: any) =>
                            new EmployerRequestHourlyRateInputModel(this, rate),
                    );
            }

            const fallbackRates = rateComponentSetDate?.minimumRates ?? [];
            return fallbackRates.sort(sortByAgeThreshold).map((rate: any) => {
                const sanitizedRate = canSetAgeSpecificHourlyRates
                    ? rate
                    : { ...rate, rate: null };
                return new EmployerRequestHourlyRateInputModel(
                    this,
                    sanitizedRate,
                );
            });
        });
        this.singleHourlyRate =
            ko.observable<EmployerRequestHourlyRateInputModel | null>(
                this.canSetAgeSpecificHourlyRates()
                    ? null
                    : this.hourlyRates()[0],
            );

        this.costCentres = ko.observableArray<CostCentre>(model.costCentres);
        this.showNewCostCentre = ko.observable<boolean>(false);
        this.selectedCostCentre = ko.observable<SelectPicker>().extend({
            validation: {
                onlyIf: () =>
                    !this.showNewCostCentre() &&
                    this.options.costCentreIsRequired,
                validator: (val: SelectPicker) => !!val,
                message: 'Kostenplaats is verplicht',
            },
            errorHandler: { source: this.errors, id: 'costCentreId' },
        });

        this.newCostCentre = ko.observable<string>().extend({
            validation: {
                onlyIf: () =>
                    this.showNewCostCentre() &&
                    this.options.costCentreIsRequired,
                validator: (val: string) => !!val,
                message: 'Kostenplaats is verplicht',
            },
            errorHandler: { source: this.errors, id: 'newCostCentre' },
        });

        if (model.costCentreId != null || model.defaultCostCentreId != null) {
            const costCentreId =
                model.costCentreId ?? model.defaultCostCentreId;
            const costCentre = this.costCentres().find(
                (c: CostCentre) => c.id === costCentreId,
            );
            if (costCentre != null) {
                this.selectedCostCentre({
                    label: costCentre.name,
                    value: costCentre.id,
                });
            }
        }

        this.mapDocument = ko.observable<RequestFileInputModel>(
            new RequestFileInputModel(AttachmentType.Map),
        );
        this.briefingDocument = ko.observable<RequestFileInputModel>(
            new RequestFileInputModel(AttachmentType.Briefing),
        );
        this.extraDocuments = ko.observableArray<RequestFileInputModel>([
            new RequestFileInputModel(AttachmentType.Other),
        ]);
        this.existingDocuments = ko.observableArray<RequestFileModel>(
            model.existingDocuments ??
                model.documents?.map((doc: any) => new RequestFileModel(doc)) ??
                [],
        );
        this.useCompanyDocuments = ko.observable<boolean>(
            model.useCompanyDocuments ?? true,
        );
        this.companyHasDocuments = ko.observable<boolean>(
            this.options.companyHasDocuments,
        );
        this.showNoCompanyDocumentsWarning = ko.pureComputed(
            () => this.useCompanyDocuments() && !this.companyHasDocuments(),
        );
        this.competentions = ko.observableArray<Competention>(
            model.competentions,
        );

        this.companyHasFavoriteEmployees = ko.observable(
            model.companyHasFavoriteEmployees,
        );

        this.hasAcceptedRegistrations = ko.observable(
            model.hasAcceptedRegistrations,
        );
        this.id = ko.observable<string>(model.id);

        this.amount = ko.observable<string>(model.amount).extend({
            required: {
                params: true,
                message: 'Aantal is verplicht',
            },
            validation: {
                validator: (val: string) => {
                    const numberValue = Number(val);

                    return numberValue > 0 && numberValue < INT32_MAX_VALUE;
                },
                message: 'Vul een geldig aantal in',
            },
            errorHandler: { source: this.errors, id: 'Amount' },
        });

        this.bonusRate = ko.observable<string>(model.bonusRate).extend({
            validation: [
                {
                    validator: (val: string) =>
                        !val || numeral.validate(val, 'nl-nl'),
                    message: 'Vul een geldig bonustarief in',
                },
            ],
        });

        this.multipleDays = ko.observable<boolean>(model.dateTo != null);
        this.isVacancy = ko.observable<boolean>(model.isVacancy || false);
        this.date = ko
            .observable<moment.Moment>(model.date || moment())
            .extend({
                validation: [
                    {
                        validator: (date: string) => !!date,
                        message: 'Datum van is verplicht',
                    },
                    {
                        validator: (date: string) =>
                            !moment(date, 'DD-MM-YYYY').isBefore(
                                moment(),
                                'day',
                            ),
                        message: 'Datum van mag niet in het verleden zijn',
                    },
                ],
            });

        this.selectedDates = ko.observableArray<moment.Moment>(
            model.selectedDates || [this.date()],
        );
        this.selectedDates.subscribe(async (dates) => {
            const rateComponentSetDate =
                dates.length === 1
                    ? dates[0]
                    : dates.sort((a, b) => (a.isBefore(b) ? 1 : -1))[0];

            await this.loadRateComponentSetDateForDate(rateComponentSetDate);
        });

        const selectedDatesMessage = ko.computed(() => {
            const today = moment().startOf('day');
            const selectedDates = this.selectedDates();

            if (selectedDates == null) {
                return '';
            }

            const pastDates = selectedDates
                .filter((date) => date.isBefore(today, 'day'))
                .map((date) => date.format('DD-MM-YYYY'));

            if (pastDates.length === 0) {
                return true;
            }

            if (
                !this.multipleDays() ||
                (pastDates.length === 1 && selectedDates.length === 1)
            ) {
                return 'Datum mag niet in het verleden zijn';
            }

            return `De volgende datums mogen niet in het verleden zijn: ${pastDates.join(', ')}`;
        });

        this.selectedDates.extend({
            validation: [
                {
                    validator: (selectedDates: moment.Moment[]) => {
                        return selectedDates.length > 0;
                    },
                    message: 'Datum is verplicht',
                },
                {
                    validator: (selectedDates: moment.Moment[]) =>
                        selectedDates.every(
                            (date) =>
                                !date.isBefore(moment().startOf('day'), 'day'),
                        ),

                    message: selectedDatesMessage(),
                },
            ],
        });

        this.dateTo = ko.observable<moment.Moment>(model.dateTo).extend({
            validation: [
                {
                    validator: (dateTo: string) => {
                        if (!this.isVacancy()) {
                            return true;
                        }

                        return !!dateTo;
                    },
                    message: 'Datum tot is verplicht',
                },
                {
                    validator: (val: string) => {
                        if (!this.isVacancy()) {
                            return true;
                        }

                        const dateTo = moment(val, 'DD-MM-YYYY');
                        const date = moment(this.date());
                        return dateTo.isSameOrAfter(date, 'days');
                    },
                    message: `Datum tot mag niet vóór Datum van zijn`,
                },
            ],
            errorHandler: { source: this.errors, id: 'EndDate' },
        });

        this.timePartFrom = ko.observable<string>(model.timePartFrom);

        const timePartFromMessage = ko.computed(() => {
            const today = moment();
            const time = this.timePartFrom();
            const selectedDates = this.selectedDates();

            const pastDates = selectedDates
                .map((date) => this.createFullDate(date, time))
                .filter((date) => date.isBefore(today))
                .map((date) => date.format('DD-MM-YYYY HH:mm'));

            if (
                !this.multipleDays() ||
                (pastDates.length === 1 && selectedDates.length === 1)
            ) {
                return 'Starttijd moet in de toekomst zijn';
            }

            return `Starttijd moet in de toekomst zijn voor: ${pastDates.join(', ')}`;
        });

        this.timePartFrom.extend({
            validation: [
                {
                    validator: (val: string) => {
                        if (this.isVacancy()) {
                            return true;
                        }

                        return !!val;
                    },
                    message: 'Starttijd is verplicht',
                },
                {
                    validator: (val: string) => {
                        if (this.isVacancy()) {
                            return true;
                        }

                        const text = `${moment().format('DD-MM-YYYY')} ${val}`;
                        const fullDate = moment(text, 'DD-MM-YYYY HH:mm');
                        return fullDate.isValid();
                    },
                    message: 'Vul een geldige startdatum in',
                },
                {
                    validator: (val: string) => {
                        if (this.isVacancy()) {
                            return true;
                        }

                        const isSingleDay = !this.multipleDays();
                        const selectedDates = this.selectedDates();

                        if (isSingleDay) {
                            const fullDate = this.createFullDate(
                                selectedDates[0],
                                val,
                            );
                            return !fullDate.isBefore(moment());
                        }

                        const dates = selectedDates.map((date) =>
                            this.createFullDate(date, val),
                        );
                        return dates.every((date) => !date.isBefore(moment()));
                    },
                    message: timePartFromMessage(),
                },
            ],
        });

        this.timePartTo = ko.observable<string>(model.timePartTo);

        const timePartToMessage = ko.computed(() => {
            const today = moment();
            const time = this.timePartTo();
            const selectedDates = this.selectedDates();

            const pastDates = selectedDates
                .map((date) => this.createFullDate(date, time))
                .filter((date) => date.isBefore(today))
                .map((date) => date.format('DD-MM-YYYY HH:mm'));

            if (
                !this.multipleDays() ||
                (pastDates.length === 1 && selectedDates.length === 1)
            ) {
                return 'Eindtijd moet in de toekomst zijn';
            }

            return `Eindtijd moet in de toekomst zijn: ${pastDates.join(', ')}`;
        });

        this.timePartTo.extend({
            validation: [
                {
                    validator: (val: string) => {
                        if (this.isVacancy()) {
                            return true;
                        }

                        return !!val;
                    },
                    message: 'Eindtijd is verplicht',
                },
                {
                    validator: (val: string) => {
                        if (this.isVacancy()) {
                            return true;
                        }

                        const text = `${moment().format('DD-MM-YYYY')} ${val}`;
                        const fullDate = moment(text, 'DD-MM-YYYY HH:mm');
                        return fullDate.isValid();
                    },
                    message: 'Vul een geldige eindtijd in',
                },
                {
                    validator: (val: string) => {
                        if (this.isVacancy()) {
                            return true;
                        }

                        let date = moment();

                        const startDateText = `${date.format('DD-MM-YYYY')} ${this.timePartFrom()}`;
                        const startDate = moment(
                            startDateText,
                            'DD-MM-YYYY HH:mm',
                        );

                        const endDateText = `${moment().format('DD-MM-YYYY')} ${val}`;
                        let endDate = moment(endDateText, 'DD-MM-YYYY HH:mm');
                        if (endDate.isBefore(date, 'hour')) {
                            endDate = endDate.add(1, 'day');
                        }

                        if (
                            startDate.isValid() &&
                            endDate.isValid() &&
                            endDate.isBefore(startDate)
                        ) {
                            return false;
                        }

                        return true;
                    },
                    message: 'Eindtijd moet na starttijd zijn',
                },
                {
                    validator: (val: string) => {
                        if (this.isVacancy()) {
                            return true;
                        }

                        const isSingleDay = !this.multipleDays();
                        const selectedDates = this.selectedDates();

                        if (isSingleDay) {
                            const date = moment();
                            let fullDate = this.createFullDate(
                                selectedDates[0],
                                val,
                            );

                            if (fullDate.isBefore(date, 'hour')) {
                                fullDate = fullDate.add(1, 'day');
                            }

                            return !fullDate.isBefore(moment());
                        }

                        const dates = selectedDates.map((date) =>
                            this.createFullDate(date, val),
                        );

                        return dates.every((date) => {
                            if (date.isBefore(moment(), 'hour')) {
                                date = date.add(1, 'day');
                            }
                            return !date.isBefore(moment());
                        });
                    },
                    message: timePartToMessage(),
                },
            ],
        });

        this.requestDuration = ko
            .pureComputed<number | null>(() => {
                if (this.timePartFrom() == null || this.timePartTo() == null) {
                    return null;
                }

                const startDate = this.getStartDate();
                const endDate = this.getEndDate();
                const duration = moment
                    .duration(moment(endDate!).diff(startDate!))
                    .asHours();
                return duration;
            })
            .extend({
                validation: [
                    {
                        validator: (duration: number | null) => {
                            if (this.isVacancy() || !duration) {
                                return true;
                            }

                            return duration >= 3;
                        },
                        message: 'Dienst moet minstens 3 uur duren',
                    },
                ],
            });

        this.selectedQualityType = ko
            .observable<string>(model.selectedQualityType)
            .extend({
                required: {
                    params: true,
                    message: 'Selecteer een type medewerker',
                },
            });
        this.name = ko.observable<string>(model.name || '').extend({
            required: {
                params: true,
                message: 'Voer een naam voor de uitvraag in',
            },
        });

        this.addressInfoExpanded = ko.observable<boolean>(
            model.addressInfoExpanded || false,
        );
        this.address = ko.observable<string>(model.address).extend({
            validation: [
                {
                    onlyIf: () => this.addressInfoExpanded(),
                    validator: (value: string) => !!value,
                    message: 'Voer een adres voor de uitvraag in',
                },
            ],
            errorHandler: { source: this.errors, id: 'Address' },
        });
        this.houseNumber = ko.observable<string>(model.houseNumber).extend({
            validation: [
                {
                    onlyIf: () => this.addressInfoExpanded(),
                    validator: (value: string) => !!value,
                    message: 'Voer een huisnummer voor de uitvraag in',
                },
            ],
        });
        this.postalCode = ko.observable<string>(model.postalCode).extend({
            validation: [
                {
                    onlyIf: () => this.addressInfoExpanded(),
                    validator: (value: string) => !!value,
                    message: 'Voer een postcode voor de uitvraag in',
                },
            ],
        });
        this.city = ko.observable<string>(model.city).extend({
            validation: [
                {
                    onlyIf: () => this.addressInfoExpanded(),
                    validator: (value: string) => !!value,
                    message: 'Voer een stad voor de uitvraag in',
                },
            ],
        });

        this.contactInfoExpanded = ko.observable<boolean>(
            model.contactInfoExpanded || false,
        );
        this.contactEmail = ko.observable<string>(model.contactEmail).extend({
            validation: [
                {
                    onlyIf: () => this.contactInfoExpanded(),
                    validator: (value: string) => !!value,
                    message: 'Voer een e-mailadres voor de uitvraag in',
                },
                {
                    onlyIf: () => this.contactInfoExpanded(),
                    validator: (val: string) => {
                        if (!val || val.length === 0) {
                            return true;
                        }
                        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
                    },
                    message: 'Email is ongeldig',
                },
            ],
            errorHandler: { source: this.errors, id: 'ContactEmail' },
        });
        this.contactPhoneNumber = ko
            .observable<string>(model.contactPhoneNumber)
            .extend({
                validation: [
                    {
                        onlyIf: () => this.contactInfoExpanded(),
                        validator: (val: string) => !!val,
                        message: 'Voer een telefoonnummer voor de uitvraag in',
                    },
                ],
                errorHandler: { source: this.errors, id: 'ContactPhoneNumber' },
            });

        this.selectedQualityType.subscribe(() =>
            this.selectedCompetentions([]),
        );

        this.selectedCompetentions = ko.observableArray<string>(
            model.selectedCompetentions ? [...model.selectedCompetentions] : [],
        );
        this.enableEmployeeClocks = ko.observable<boolean>(
            model.enableEmployeeClocks || true,
        );

        this.travelExpenses = ko.observable<boolean>(
            model.travelExpenses || false,
        );
        this.forFavorites = ko.observable<boolean>(model.forFavorites || false);
        this.collaborationPreferenceAllowed = ko.observable<boolean>(
            model.collaborationPreferenceAllowed || false,
        );

        this.imageInfoExpanded = ko.observable<boolean>(
            model.imageInfoExpanded || false,
        );

        this.imageHash = ko.observable<string>(model.imageHash || '');

        this.image = ko.observable<any>({
            dataURL: ko.observable<any>(model.imageDataURL),
        });

        this.image().dataURL.subscribe(() => {
            this.imageHash('');
        });

        if (this.imageHash()) {
            this.image().file = ko.observable({ name: ' ' });
        }

        this.imageSrc = ko.pureComputed(() => {
            if (this.image().dataURL()) {
                return this.image().dataURL();
            }

            if (this.imageHash()) {
                return resolveCdnUrl(this.imageHash());
            }

            return '';
        });

        this.existingImages =
            ko.observableArray<AddOrEditRequestImageInputModel>(
                model.existingImages ??
                    model.images?.map(
                        (img: any) => new AddOrEditRequestImageInputModel(img),
                    ) ??
                    [],
            );
        this.addImages = ko.observable<boolean>(
            this.existingImages().length > 0,
        );
        this.images = ko.observableArray<AddOrEditRequestImageInputModel>(
            this.addImages()
                ? orderBy(this.existingImages() ?? [], 'order', 'asc')
                : [
                      new AddOrEditRequestImageInputModel({
                          order: this.existingImages().length,
                      }),
                  ],
        );

        this.selectedQualityTypeName = ko.pureComputed(() => {
            return this.qualityTypes().find(
                (qt: QualityType) => qt.id === this.selectedQualityType(),
            )!.name;
        });
        this.selectedQualityTypeExplanation = ko.pureComputed(
            () =>
                this.qualityTypes().find(
                    (qt: QualityType) => qt.id === this.selectedQualityType(),
                )?.explanation ?? null,
        );

        this.filteredCompetentions = ko.pureComputed(() => {
            const competentions = this.competentions()
                .filter(
                    (c: Competention) =>
                        this.selectedQualityType() &&
                        c.qualityTypeIds &&
                        c.qualityTypeIds.includes(this.selectedQualityType()),
                )
                .sort(
                    (a: Competention, b: Competention) =>
                        a.examOrder - b.examOrder,
                );
            return competentions;
        });

        this.extraInfo = ko.observable<string>(model.extraInfo || '');
        this.clothingRequirements = ko.observable<string>(
            model.clothingRequirements,
        );

        this.previewImageSrc = ko.pureComputed(() => {
            const imageHash =
                this.imageHash() != null && this.imageHash() != ''
                    ? resolveCdnUrl(this.imageHash())
                    : resolveCdnUrl(this.options.companyAvatarHash);
            return this.imageInfoExpanded() && this.image().dataURL()
                ? this.imageSrc()
                : imageHash;
        });

        this.preview = ko.pureComputed<PreviewModel>(() => {
            let previewModel: PreviewModel = {
                title: this.name(),
                subTitle: this.selectedQualityType()
                    ? this.selectedQualityTypeName()
                    : 'Type medewerker',
                imageSrc: this.previewImageSrc(),
                isVacancy: this.isVacancy(),
                hourlyRateText: `${numeral(this.hourlyRates().length > 0 ? this.hourlyRates()[0].rate() : 0).format('$0,0.00')}/u`,
                cityText: this.city(),
                timeText: undefined,
            };

            if (!this.isVacancy()) {
                const startTime = !!this.timePartFrom()
                    ? this.timePartFrom()
                    : 'Starttijd';
                const endTime = !!this.timePartTo()
                    ? this.timePartTo()
                    : 'Eindtijd';

                previewModel = {
                    ...previewModel,
                    dateText: !!this.date()
                        ? this.date().format('dd D MMM')
                        : 'Datum',
                    timeText: `${startTime} - ${endTime}`,
                };
            } else {
                const hoursAvailableMin = this.hoursAvailableMin();
                const hoursAvailableMax = this.hoursAvailableMax();

                let hoursAvailable = '';
                if (hoursAvailableMin && hoursAvailableMax) {
                    hoursAvailable =
                        hoursAvailableMin !== hoursAvailableMax
                            ? `${hoursAvailableMin} - ${hoursAvailableMax}`
                            : `${hoursAvailableMin}`;
                } else if (hoursAvailableMin) {
                    hoursAvailable = `${hoursAvailableMin} - Max`;
                } else if (hoursAvailableMax) {
                    hoursAvailable = `Min - ${hoursAvailableMax}`;
                }

                const daysOfWeek = getDaysOfWeekFormatted(this.daysOfWeek());
                const startDateText = !!this.date()
                    ? moment(this.date()).format('D MMM')
                    : 'Van-datum';
                const endDateText = !!this.dateTo()
                    ? moment(this.dateTo()).format('D MMM')
                    : 'Tot-datum';
                const dateText = moment(this.date()).isSame(
                    moment(this.dateTo()),
                    'day',
                )
                    ? startDateText
                    : `${startDateText} - ${endDateText}`;

                previewModel = {
                    ...previewModel,
                    title: `${previewModel.subTitle} ${!!hoursAvailable ? `(${hoursAvailable} uur)` : '(uren beschikbaar)'}`,
                    subTitle: !!daysOfWeek ? daysOfWeek : 'Dagen',
                    dateText,
                };
            }

            return previewModel;
        });

        this.closeDate = ko.observable<moment.Moment>(model.closeDate).extend({
            validation: [
                {
                    validator: (val: string) => {
                        if (!this.isVacancy()) {
                            return true;
                        }

                        return !!val;
                    },
                    message: 'Sluitdatum is verplicht',
                },
                {
                    validator: (val: string) => {
                        if (!this.isVacancy() || val == null) {
                            return true;
                        }

                        const closeDate = moment(val, 'DD-MM-YYYY', true);
                        const dateTo = moment(
                            this.dateTo(),
                            'DD-MM-YYYY',
                            true,
                        );
                        return closeDate.isSameOrBefore(dateTo, 'day');
                    },
                    message: `Sluitdatum mag niet na de Tot-datum zijn`,
                },
            ],
            errorHandler: { source: this.errors, id: 'CloseDate' },
        });

        this.daysOfWeek = ko
            .observableArray<DaysOfWeek>(model.daysOfWeek)
            .extend({
                errorHandler: { source: this.errors, id: 'DaysOfWeek' },
            });

        this.hoursAvailableMax = ko
            .observable<number>(model.hoursAvailableMax)
            .extend({
                validation: [
                    {
                        validator: (val: string) => {
                            if (!this.isVacancy()) {
                                return true;
                            }

                            return !!val;
                        },
                        message: 'Max is verplicht',
                    },
                    {
                        validator: (val: string) => {
                            if (!this.isVacancy() || val == null) {
                                return true;
                            }

                            const numberValue = Number(val);
                            return (
                                numberValue > 0 && numberValue < INT32_MAX_VALUE
                            );
                        },
                        message: 'Vul een geldig max uren in',
                    },
                ],
                errorHandler: { source: this.errors, id: 'HoursAvailableMax' },
            });

        this.hoursAvailableMin = ko
            .observable<number>(model.hoursAvailableMin)
            .extend({
                validation: [
                    {
                        validator: (val: string) => {
                            if (!this.isVacancy()) {
                                return true;
                            }

                            return !!val;
                        },
                        message: 'Min is verplicht',
                    },
                    {
                        validator: (val: string) => {
                            if (!this.isVacancy() || val == null) {
                                return true;
                            }

                            const numberValue = Number(val);
                            return (
                                numberValue > 0 && numberValue < INT32_MAX_VALUE
                            );
                        },
                        message: 'Vul een geldig min uren in',
                    },
                    {
                        validator: (val: string) => {
                            if (
                                !this.isVacancy() ||
                                val == null ||
                                this.hoursAvailableMax() == null
                            ) {
                                return true;
                            }
                            return Number(val) <= this.hoursAvailableMax();
                        },
                        message: 'Min moet kleiner zijn dan Max',
                    },
                ],
                errorHandler: { source: this.errors, id: 'HoursAvailableMin' },
            });
    }

    async onLeavePostalCodeOrHouseNumberField() {
        if (
            this.postalCode() &&
            this.postalCode().length > 0 &&
            this.houseNumber() &&
            this.houseNumber().length > 0
        ) {
            const result = await lookupAddress(
                this.postalCode(),
                this.houseNumber(),
            );

            if (result.success) {
                this.address(result.street);
                this.city(result.town);
            }
        }
    }

    toggleCostCentre() {
        this.showNewCostCentre(!this.showNewCostCentre());
    }

    getStartDate(): moment.Moment | null {
        return this.getDate(true);
    }

    getEndDate(): moment.Moment | null {
        return this.getDate(false);
    }

    isDateFilledIn(): boolean {
        return this.date() !== undefined;
    }

    formatDate(): string {
        let date: moment.Moment = this.getStartDate()!;
        return date
            ? this.isVacancy()
                ? `${date.format('DD/MM/YYYY')} - ${this.dateTo().format('DD/MM/YYYY')}`
                : date.format('dddd DD/MM/YYYY')
            : '';
    }

    formatTime(): string {
        const startDate = this.getStartDate();
        const endDate = this.getEndDate();

        if (!startDate || !endDate) {
            return '';
        }

        return `${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`;
    }

    formatRate(rate: string): string {
        return `€${numeral(rate).format('0,0.00')}/u`;
    }

    formatSelectedCompetentions(): string {
        let selectedCompetentionDescriptions = this.selectedCompetentions()
            .filter((cId: string) =>
                this.competentions()
                    .find((c: Competention) => c.id === cId)!
                    .qualityTypeIds.includes(this.selectedQualityType()),
            )
            .map(
                (cId: string) =>
                    this.competentions().find(
                        (c: Competention) => c.id === cId,
                    )!.description,
            );

        return selectedCompetentionDescriptions.length === 0
            ? 'Geen'
            : selectedCompetentionDescriptions.join(', ');
    }

    addDocument() {
        const currentDocuments = this.extraDocuments();
        currentDocuments.push(new RequestFileInputModel(AttachmentType.Other));
        this.extraDocuments(currentDocuments);
    }

    removeDocument(index: number) {
        const currentDocuments = this.extraDocuments();
        currentDocuments.splice(index, 1);
        this.extraDocuments(currentDocuments);
    }

    removeExistingDocument(id: string) {
        const currentDocuments = this.existingDocuments();
        const documentToRemove = currentDocuments.find((doc) => doc.id === id);
        if (documentToRemove) {
            currentDocuments.splice(
                currentDocuments.indexOf(documentToRemove),
                1,
            );
        }
        this.existingDocuments(currentDocuments);
    }

    addImage() {
        const currentImages = this.images();

        const lastImage = currentImages[currentImages.length - 1];
        if (lastImage?.imageSrc() === requestImagePlaceholder) {
            return;
        }

        currentImages.push(
            new AddOrEditRequestImageInputModel({
                order: currentImages.length,
            }),
        );
        this.images(currentImages);
    }

    removeImage(index: number, id?: string) {
        const currentImages = this.images();
        currentImages.splice(index, 1);
        currentImages.forEach((img, index) => (img.order = index));
        this.images(currentImages);

        if (id != null) {
            this.removeExistingImage(id);
        }
    }

    removeExistingImage(id: string) {
        const currentImages = this.existingImages();
        const imageToRemove = currentImages.find((img) => img.id === id);
        if (imageToRemove) {
            currentImages.splice(currentImages.indexOf(imageToRemove), 1);
        }
        this.existingImages(currentImages);
    }

    moveImage(direction: 'left' | 'right', index: number) {
        const images = this.images();
        const targetIndex = direction === 'left' ? index - 1 : index + 1;

        [images[index], images[targetIndex]] = [
            images[targetIndex],
            images[index],
        ];
        images[index].order = index;
        images[targetIndex].order = targetIndex;

        this.images(images);
    }

    async loadRateComponentSetDateForDate(date: moment.Moment) {
        const response = await fetch(
            `/Employer/RateComponentSetDate/ondate?date=${date.format('YYYY-MM-DD')}`,
        );

        if (response.ok && response.data) {
            this.rateComponentSetDate(response.data);
        }

        return;
    }

    copy(): RequestModel {
        return new RequestModel(
            {
                qualityTypes: this.qualityTypes(),
                competentions: this.competentions(),
                id: this.id(),
                amount: this.amount(),
                rateComponentSetDate: this.rateComponentSetDate(),
                hourlyRates: this.hourlyRates().map((hourlyRate) => ({
                    id: hourlyRate.id(),
                    rate: hourlyRate.rate(),
                    ageThreshold: hourlyRate.ageThreshold(),
                    minimumRate: hourlyRate.minimumRate(),
                })),
                bonusRate: this.bonusRate(),
                date: this.date(),
                selectedDates: this.selectedDates(),
                timePartFrom: !this.isVacancy() ? this.timePartFrom() : null,
                timePartTo: !this.isVacancy() ? this.timePartTo() : null,
                selectedQualityType: this.selectedQualityType(),
                selectedCompetentions: this.selectedCompetentions(),
                enableEmployeeClocks: this.enableEmployeeClocks(),
                travelExpenses: this.travelExpenses(),
                forFavorites: this.forFavorites(),
                collaborationPreferenceAllowed:
                    this.collaborationPreferenceAllowed(),
                clothingRequirements: this.clothingRequirements(),
                imageInfoExpanded: this.imageInfoExpanded(),
                imageDataURL: this.image().dataURL(),
                imageHash: this.imageHash(),
                images: this.existingImages().map(
                    (img) => new AddOrEditRequestImageInputModel(img),
                ),
                name: this.name(),
                extraInfo: this.extraInfo(),
                addressInfoExpanded: this.addressInfoExpanded(),
                address: this.address(),
                houseNumber: this.houseNumber(),
                city: this.city(),
                postalCode: this.postalCode(),
                contactInfoExpanded: this.contactInfoExpanded(),
                contactEmail: this.contactEmail(),
                contactPhoneNumber: this.contactPhoneNumber(),
                costCentres: this.costCentres(),
                costCentreId: this.selectedCostCentre()?.value,
                documents: this.existingDocuments().map(
                    (doc) => new RequestFileModel(doc),
                ),
                useCompanyDocuments: this.useCompanyDocuments(),
                companyHasFavoriteEmployees: this.companyHasFavoriteEmployees(),
                isVacancy: this.isVacancy(),
                dateTo: this.isVacancy() ? this.dateTo() : undefined,
                closeDate: this.isVacancy() ? this.closeDate() : undefined,
                daysOfWeek: this.isVacancy() ? this.daysOfWeek() : [],
                hoursAvailableMin: this.isVacancy()
                    ? this.hoursAvailableMin()
                    : undefined,
                hoursAvailableMax: this.isVacancy()
                    ? this.hoursAvailableMax()
                    : undefined,
            },
            this.options,
        );
    }

    toRequestData(): any {
        const startDate = this.isVacancy()
            ? (this.date()?.format('YYYY-MM-DD') ?? '')
            : this.getStartDate()
              ? this.getStartDate()!.format('YYYY-MM-DD HH:mm')
              : '';

        const endDate = this.isVacancy()
            ? (this.dateTo()?.format('YYYY-MM-DD') ?? '')
            : this.getEndDate()
              ? this.getEndDate()!.format('YYYY-MM-DD HH:mm')
              : '';

        const data = new FormData();
        data.append('id', this.id());
        data.append('name', this.name());
        data.append('amount', this.amount());

        const singleHourlyRate = this.singleHourlyRate();
        this.hourlyRates().forEach((hourlyRate, index) => {
            if (hourlyRate.id() != null) {
                data.append(`hourlyRates[${index}].id`, hourlyRate.id()!);
            }
            const rate = singleHourlyRate?.rate() ?? hourlyRate.rate();
            data.append(
                `hourlyRates[${index}].rate`,
                numeral(rate).format('0.00'),
            );
            data.append(
                `hourlyRates[${index}].ageThreshold`,
                hourlyRate.ageThreshold().toString(),
            );
        });

        !!this.bonusRate() &&
            data.append('bonusRate', numeral(this.bonusRate()).format('0.00'));

        data.append('startDate', startDate);
        data.append('endDate', endDate);

        if (!this.isVacancy() && this.multipleDays()) {
            const selectedDates = this.selectedDates().map((date) => {
                const timeParts = [this.timePartFrom(), this.timePartTo()];
                const dateFormat = `${date.format('MM-DD-YYYY')}`;
                const startDate = moment(
                    `${dateFormat} ${timeParts[0]}`,
                    'MM-DD-YYYY HH:mm',
                );
                let endDate = moment(
                    `${dateFormat} ${timeParts[1]}`,
                    'MM-DD-YYYY HH:mm',
                );

                if (endDate.isBefore(startDate, 'hour')) {
                    endDate = endDate.add(1, 'day');
                }

                return {
                    startDate: startDate.format('YYYY-MM-DD HH:mm'),
                    endDate: endDate.format('YYYY-MM-DD HH:mm'),
                };
            });

            selectedDates.forEach((period, index) => {
                data.append(`periods[${index}].startDate`, period.startDate);
                data.append(`periods[${index}].endDate`, period.endDate);
            });
        }

        data.append('qualityTypeId', this.selectedQualityType());
        data.append(
            'enableEmployeeClocks',
            this.enableEmployeeClocks().toString(),
        );
        data.append('travelExpenses', this.travelExpenses().toString());
        data.append('forFavorites', this.forFavorites().toString());
        data.append(
            'collaborationPreferenceAllowed',
            this.collaborationPreferenceAllowed().toString(),
        );
        data.append('address', this.address() ?? this.options.companyAddress);
        data.append('city', this.city() ?? this.options.companyCity);
        data.append(
            'houseNumber',
            this.houseNumber() ?? this.options.companyHouseNumber,
        );
        data.append(
            'postalCode',
            this.postalCode() ?? this.options.companyPostalCode,
        );
        data.append(
            'contactEmail',
            this.contactEmail() ?? this.options.companyEmail,
        );
        data.append(
            'contactPhoneNumber',
            this.contactPhoneNumber() ?? this.options.companyPhoneNumber,
        );

        data.append('isVacancy', this.isVacancy().toString());
        if (this.isVacancy()) {
            if (this.closeDate() != null) {
                data.append('closeDate', this.closeDate().format('YYYY-MM-DD'));
            }

            this.daysOfWeek().forEach((value, index) => {
                data.append(`daysOfWeek[${index}]`, value.toString());
            });

            if (this.hoursAvailableMin() != null) {
                data.append(
                    'hoursAvailableMin',
                    this.hoursAvailableMin().toString(),
                );
            }

            if (this.hoursAvailableMax() != null) {
                data.append(
                    'hoursAvailableMax',
                    this.hoursAvailableMax().toString(),
                );
            }
        }

        if (this.imageInfoExpanded()) {
            if (this.image().file && this.image().file()) {
                data.append('image', this.image().file());
            } else if (this.image().dataURL()) {
                const blob = dataUriToBlob(this.image().dataURL());
                data.append('image', blob);
            } else if (this.imageHash() != null && this.imageHash() != '') {
                fetch(this.imageSrc())
                    .then((res) => res.blob())
                    .then((blob) => {
                        data.append('image', blob);
                    });
            }
            data.append('imageHash', this.imageHash());
        }

        this.extraInfo() != null && data.append('extraInfo', this.extraInfo());
        this.clothingRequirements() != null &&
            data.append('clothingRequirements', this.clothingRequirements());
        if (this.selectedCompetentions().length > 0) {
            this.selectedCompetentions().forEach((c: string) =>
                data.append('competentions[]', c),
            );
        }

        if (this.showNewCostCentre() && this.newCostCentre() != null) {
            data.append('newCostCentre', this.newCostCentre());
        } else if (
            !this.showNewCostCentre() &&
            this.selectedCostCentre() != null
        ) {
            data.append('costCentreId', this.selectedCostCentre().value);
        }

        data.append(
            'useCompanyDocuments',
            this.useCompanyDocuments().toString(),
        );

        if (!this.useCompanyDocuments()) {
            let documents = this.extraDocuments().filter(
                (doc) => doc.file().file() != null,
            );

            if (this.mapDocument().file()?.file() != null) {
                documents.push(this.mapDocument());
            }

            if (this.briefingDocument().file()?.file() != null) {
                documents.push(this.briefingDocument());
            }

            documents.forEach((doc, index) => {
                data.append(`documents[${index}].file`, doc.file().file());
                data.append(`documents[${index}].type`, doc.type.toString());
            });

            this.existingDocuments().forEach((doc) => {
                data.append('existingDocumentIds', doc.id);
            });
        }

        if (this.addImages()) {
            const newImages = this.images().filter(
                (img) => img.id == null && img.file() != null,
            );
            const existingImages = this.existingImages().filter(
                (img) => img.id != null,
            );

            newImages.forEach((img, index) => {
                data.append(`newImages[${index}].file`, img.file());
                data.append(`newImages[${index}].order`, img.order.toString());
            });

            existingImages.forEach((img, index) => {
                data.append(`existingImages[${index}].fileId`, img.id);
                data.append(
                    `existingImages[${index}].order`,
                    img.order.toString(),
                );
            });
        }

        return data;
    }

    private getDate(startDate: boolean): moment.Moment | null {
        if (!this.date()) {
            return null;
        }

        const timeParts = startDate
            ? [this.timePartFrom(), this.timePartTo()]
            : [this.timePartTo(), this.timePartFrom()];

        const dateFormat = `${(this.isVacancy() ? this.date() : this.selectedDates()[0]).format('MM-DD-YYYY')}`;
        let date1 = moment(`${dateFormat} ${timeParts[0]}`, 'MM-DD-YYYY HH:mm');
        const date2 = moment(
            `${dateFormat} ${timeParts[1]}`,
            'MM-DD-YYYY HH:mm',
        );

        //If the enddate's time part is before the startdate's time part, we can assume the end date is the next day
        if (!startDate && date1.isBefore(date2, 'hour')) {
            date1 = date1.add(1, 'day');
        }

        if (!date1.isValid()) {
            return null;
        }

        return date1;
    }

    private createFullDate(date: moment.Moment, time: string): moment.Moment {
        const fullDate = `${date.format('DD-MM-YYYY')} ${time}`;
        return moment(fullDate, 'DD-MM-YYYY HH:mm');
    }
}

export class AddOrEditRequestImageInputModel {
    id: string;
    name: string;
    cdnHash: string;

    order: number;
    file: KnockoutObservable<File>;
    dataURL: KnockoutObservable<string>;

    imageAlt: KnockoutComputed<string>;
    imageSrc: KnockoutComputed<string>;

    buttonClass: KnockoutComputed<string | null>;
    customFileInputBinding: KnockoutObservable<any>;

    constructor(model: any) {
        this.id = model.id;
        this.name = model.name;
        this.cdnHash = model.cdnHash;

        this.order = model.order;
        this.file = ko.observable<File>(null);
        this.dataURL = ko.observable<string>();

        this.imageAlt = ko.pureComputed(
            () => this.file()?.name ?? this.name ?? 'Add photo',
        );
        this.imageSrc = ko.pureComputed(() =>
            this.cdnHash
                ? resolveCdnUrl(this.cdnHash)
                : this.dataURL() || requestImagePlaceholder,
        );

        this.buttonClass = ko.pureComputed(() =>
            this.cdnHash ? null : 'move-button',
        );
        this.customFileInputBinding = ko.observable({
            wrapperClass: 'form-file',
            buttonClass: this.buttonClass(),
            buttonText: '<i class="fa fa-plus"></i>',
            changeButtonText: '<i class="fa fa-pencil"></i>',
            clearButton: false,
        });
    }
}

export class RequestFileInputModel {
    file: KnockoutObservable<any>;
    type: AttachmentType;

    constructor(type: AttachmentType) {
        this.file = ko.observable<any>();
        this.type = type;
    }
}

export class RequestFileModel {
    id: string;
    name: string;
    type: AttachmentType;

    constructor(model: any) {
        this.id = model.id;
        this.name = model.name;
        this.type = model.type;
    }
}

export enum DaysOfWeek {
    Monday = 1,
    Tuesday = 2,
    Wednesday = 4,
    Thursday = 8,
    Friday = 16,
    Saturday = 32,
    Sunday = 64,
}

export const getDaysOfWeekFormatted = (daysOfWeek: DaysOfWeek[]) => {
    return daysOfWeek
        .sort((a: DaysOfWeek, b: DaysOfWeek) => a - b)
        .map((day: DaysOfWeek, index: number) => {
            let dayLabel = '';

            switch (parseInt(day.toString())) {
                case DaysOfWeek.Monday:
                    dayLabel = 'Ma';
                    break;
                case DaysOfWeek.Tuesday:
                    dayLabel = 'Di';
                    break;
                case DaysOfWeek.Wednesday:
                    dayLabel = 'Wo';
                    break;
                case DaysOfWeek.Thursday:
                    dayLabel = 'Do';
                    break;
                case DaysOfWeek.Friday:
                    dayLabel = 'Vr';
                    break;
                case DaysOfWeek.Saturday:
                    dayLabel = 'Za';
                    break;
                case DaysOfWeek.Sunday:
                    dayLabel = 'Zo';
                    break;
                default:
                    break;
            }

            return index === 0
                ? dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)
                : dayLabel.toLowerCase();
        })
        .join(', ');
};

export class RequestHourlyRateAndCommissionRuleCombinedInputModel {
    requestHourlyRateId: KnockoutObservable<string>;
    requestCommissionRuleId: KnockoutObservable<string>;

    ageThreshold: KnockoutObservable<number>;
    rate: KnockoutObservable<number>;
    percentage: KnockoutObservable<number>;

    netHourlyRate: KnockoutObservable<string>;
    loadingNetHourlyRate: KnockoutObservable<boolean>;
    netHourlyRateError: KnockoutObservable<boolean>;

    private startDate?: KnockoutObservable<moment.Moment>;

    constructor(
        model: RequestHourlyRateAndCommissionRuleCombined,
        startDate?: KnockoutObservable<moment.Moment>,
    ) {
        this.requestHourlyRateId = ko.observable(model.requestHourlyRateId);
        this.requestCommissionRuleId = ko.observable(
            model.requestCommissionRuleId,
        );
        this.ageThreshold = ko.observable(model.ageThreshold);
        this.startDate = startDate;

        this.netHourlyRate = ko.observable('');
        this.loadingNetHourlyRate = ko.observable(false);
        this.netHourlyRateError = ko.observable(false);

        this.rate = ko.observable(model.rate).extend({
            required: {
                params: true,
                message: 'Uurtarief is verplicht',
            },
            validation: [
                {
                    validator: (val: string) => {
                        return numeral.validate(val, 'nl-nl');
                    },
                    message: 'Vul een geldig uurtarief in',
                },
            ],
        });

        this.percentage = ko.observable(model.percentage).extend({
            required: {
                params: true,
                message: 'Percentage is verplicht',
            },
            validation: [
                {
                    validator: (val: string) => {
                        return numeral.validate(val, 'nl-nl');
                    },
                    message: 'Vul een geldig percentage in',
                },
                {
                    validator: (val: string) => {
                        const numberValue = numeral(val);

                        return (
                            numberValue.value() >= 0 &&
                            numberValue.value() <= 100
                        );
                    },
                    message: () => `Percentage moet tussen 0% en 100% zijn`,
                },
            ],
        });

        this.rate.subscribe(() => this.fetchNetHourlyRate());
        this.percentage.subscribe(() => this.fetchNetHourlyRate());
        if (startDate) {
            startDate.subscribe(() => this.fetchNetHourlyRate());
        }
        void this.fetchNetHourlyRate();
    }

    private fetchNetHourlyRate = async () => {
        if (!this.startDate) {
            return;
        }

        const rateValue = ko.ignoreDependencies(() =>
            numeral(this.rate()).value(),
        );
        const percentageValue = ko.ignoreDependencies(() =>
            numeral(this.percentage()).value(),
        );
        const startDateValue = ko.ignoreDependencies(() => this.startDate!());

        if (
            rateValue == null ||
            percentageValue == null ||
            startDateValue == null ||
            !startDateValue.isValid()
        ) {
            this.netHourlyRate('');
            this.netHourlyRateError(false);
            return;
        }

        this.loadingNetHourlyRate(true);
        this.netHourlyRateError(false);

        try {
            const query = new URLSearchParams({
                age: this.ageThreshold().toString(),
                date: startDateValue.format('YYYY-MM-DD'),
                hourlyRate: rateValue.toString(),
                commissionPercentage: percentageValue.toString(),
            });

            const response = await fetch(
                `/Admin/Salary/CalculateHourlyRate?${query.toString()}`,
            );

            if (response.ok && response.data?.netHourlyRate != null) {
                this.netHourlyRate(
                    numeral(response.data.netHourlyRate).format('0,0.00'),
                );
                this.netHourlyRateError(false);
                return;
            }

            this.netHourlyRate('-');
            this.netHourlyRateError(true);
        } catch (error) {
            this.netHourlyRate('-');
            this.netHourlyRateError(true);
        } finally {
            this.loadingNetHourlyRate(false);
        }
    };
}
